import { useEffect, useState, useContext, useRef } from "react";
import PropTypes from "prop-types";

import { withALErrorBoundary } from "@helpers/ErrorBoundary/ALErrorBoundary";
import { HiddenElementsContext } from "@context/HiddenElements";
import { useProductPricing } from "@hooks/usePrices";
import { useTracking } from "@context/Tracking";
import { ALButtonV2 } from "@components/ALComponents";
import ProductElement from "@components/ProductElement";
import { ComponentType } from "@constants/ComponentType";
import { cn } from "@helpers/general";
import IconClose from "@components/icons/svgs/close-delete.svg";

function OptionalProductsDrawer({
  isVisible,
  originProductTitle, // the title of the product that the user is currently viewing (for tracking purposes)
  optionalProductsTitle, // the contentful title for the optional product collection, i.e. "Style with charms"
  initialProducts,
  allProducts = [],
  onSave,
  onClose,
}) {
  const { show, hide } = useContext(HiddenElementsContext);
  const { trackOptionalProductsDrawerAdd, trackOptionalProductsDrawerRemove } = useTracking();

  const [selectedProducts, setSelectedProducts] = useState([]);

  const drawerRef = useRef(null);

  useEffect(() => {
    if (initialProducts) {
      setSelectedProducts(initialProducts);
    }
  }, [initialProducts]);

  useEffect(() => {
    if (isVisible) {
      drawerRef?.current?.focus();
      hide();
    } else {
      show();
    }
  }, [isVisible, show, hide]);

  const { products: productsWithPrices, isInitialLoading: pricesLoading } = useProductPricing({
    products: allProducts,
  });

  const onToggleSelectHandle = (handle, prices) => {
    if (selectedProducts.map((product) => product.handle).includes(handle)) {
      setSelectedProducts(selectedProducts.filter((p) => p.handle !== handle));
      trackOptionalProductsDrawerRemove({
        originProductTitle,
        handle,
        price: prices.finalPrice,
        currency: prices.currencyCode,
      });
    } else {
      const productToAdd = productsWithPrices.find((p) => p.node.handle === handle)?.shopify;
      if (productToAdd) {
        setSelectedProducts([...selectedProducts, { ...productToAdd, prices }]);
        trackOptionalProductsDrawerAdd({
          originProductTitle,
          handle,
          price: prices.finalPrice,
          currency: prices.currencyCode,
        });
      } else {
        console.warn("Optional product select failed: product not found");
      }
    }
  };

  let confirmationText = "Confirm selection";
  if (selectedProducts.length > 1) {
    confirmationText = `Confirm ${selectedProducts.length} selections`;
  } else if (selectedProducts.length === 1) {
    confirmationText = "Confirm 1 selection";
  }

  return (
    <div className="relative" data-testid="optional-products-drawer">
      <div
        ref={drawerRef}
        className={cn(
          "fixed top-0 left-0 w-full h-full bg-[rgba(0,0,0,0.5)] z-[999999] transition-opacity duration-200 ease-linear",
          isVisible ? "block" : "hidden"
        )}
        onClick={onClose}
        role="button"
        tabIndex={0}
        aria-label="Close products drawer"
        onKeyDown={(e) => e.key === "Escape" && onClose()}
      />

      <div
        className={cn(
          "fixed top-0 right-0 flex flex-col bg-white z-[999999] h-full w-screen overflow-y-scroll overscroll-contain transition-transform duration-[600ms] ease-[cubic-bezier(0,0.57,0.55,1.07)]",
          "desktop:w-[450px] mobile:w-[100vw]",
          isVisible ? "translate-x-0" : "translate-x-[calc(100%+10px)]"
        )}
      >
        <div className="p-[20px] mobile:py-[17px] mobile:px-[15px] sticky top-0 flex justify-between items-center z-[100] bg-white border-b border-dark-grey">
          <h4 className="typo-h2 font-bold">{optionalProductsTitle}</h4>
          <IconClose
            fill="none"
            stroke="#000000"
            width="15px"
            height="15px"
            onClick={onClose}
            alt="close"
            className="cursor-pointer"
          />
        </div>

        <div className="flex flex-wrap justify-between gap-y-[30px] gap-x-0 p-[20px] mobile:p-[15px]">
          {productsWithPrices.map((product) => (
            <ProductElement
              key={product.node.handle}
              product={product}
              pricesLoading={pricesLoading}
              isSelected={selectedProducts
                .map((selectedProduct) => selectedProduct.handle)
                .includes(product.node.handle)}
              onToggle={onToggleSelectHandle}
              componentType={ComponentType.CP_OPTIONAL_PRODUCTS}
            />
          ))}
        </div>

        <div className="p-[20px] mobile:p-[15px] sticky bottom-0 w-inherit bg-white shadow-[0_-3px_15px_3px_rgba(0,0,0,0.1)] z-10">
          <ALButtonV2
            size="large"
            variant="primary"
            fullWidth
            onClick={() => {
              onSave(selectedProducts);
            }}
          >
            {confirmationText}
          </ALButtonV2>
        </div>
      </div>
    </div>
  );
}

OptionalProductsDrawer.propTypes = {
  isVisible: PropTypes.bool,
  originProductTitle: PropTypes.string,
  optionalProductsTitle: PropTypes.string,
  initialProducts: PropTypes.array,
  allProducts: PropTypes.arrayOf(
    PropTypes.shape({
      node: PropTypes.shape({
        handle: PropTypes.string,
      }),
    })
  ),
  onSave: PropTypes.func,
  onClose: PropTypes.func,
};

export default withALErrorBoundary({
  name: "OptionalProductsDrawer",
  priority: "P3",
})(OptionalProductsDrawer);
